// Hook para gerenciar Web Push subscription do navegador atual (portal cidadao).
// Identico ao admin mas usa supabase do portal.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type StatusPush = 'nao-suportado' | 'sem-permissao' | 'negado' | 'inscrito' | 'nao-inscrito' | 'carregando';

export function useWebPush() {
  const [status, setStatus] = useState<StatusPush>('carregando');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const suportado = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;

  const verificar = useCallback(async () => {
    if (!suportado) { setStatus('nao-suportado'); return; }
    try {
      if (Notification.permission === 'denied') { setStatus('negado'); return; }
      const reg = await obterRegistroSW();
      if (!reg) {
        // Sem service worker registrado (ex.: dev sem PWA) — não trava o "carregando".
        setStatus(Notification.permission === 'granted' ? 'nao-inscrito' : 'sem-permissao');
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        setStatus('inscrito');
      } else if (Notification.permission === 'granted') {
        setStatus('nao-inscrito');
      } else {
        setStatus('sem-permissao');
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao verificar push');
      setStatus('nao-inscrito');
    }
  }, [suportado]);

  useEffect(() => {
    void verificar();
  }, [verificar]);

  const inscrever = useCallback(async () => {
    if (!suportado) return false;
    if (!VAPID_PUBLIC_KEY) {
      setErro('VITE_VAPID_PUBLIC_KEY não configurada');
      return false;
    }
    setCarregando(true);
    setErro(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setStatus(perm === 'denied' ? 'negado' : 'sem-permissao');
        return false;
      }

      const reg = await obterRegistroSW();
      if (!reg) {
        setErro('Notificações push indisponíveis neste ambiente (sem service worker).');
        return false;
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as ArrayBuffer,
      });

      const json = sub.toJSON();
      const { data, error } = await supabase.rpc('web_push_registrar', {
        p_endpoint: sub.endpoint,
        p_p256dh: json.keys?.p256dh ?? '',
        p_auth: json.keys?.auth ?? '',
        p_user_agent: navigator.userAgent,
      });
      if (error) throw error;
      if ((data as { erro?: string })?.erro) throw new Error((data as { erro: string }).erro);

      setStatus('inscrito');
      return true;
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao inscrever');
      return false;
    } finally {
      setCarregando(false);
    }
  }, [suportado]);

  const desinscrever = useCallback(async () => {
    if (!suportado) return false;
    setCarregando(true);
    setErro(null);
    try {
      const reg = await obterRegistroSW();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await supabase.rpc('web_push_desregistrar', { p_endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setStatus(Notification.permission === 'granted' ? 'nao-inscrito' : 'sem-permissao');
      return true;
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao desinscrever');
      return false;
    } finally {
      setCarregando(false);
    }
  }, [suportado]);

  return { status, carregando, erro, suportado, inscrever, desinscrever, verificar };
}

/**
 * Obtém o registro do service worker sem travar: `serviceWorker.ready` fica pendente
 * para sempre quando nenhum SW está registrado (ex.: localhost dev sem PWA). Usamos
 * getRegistration() (resolve na hora) e, se houver SW registrando, esperamos `ready`
 * com timeout curto. Retorna null quando não há SW disponível.
 */
async function obterRegistroSW(timeoutMs = 3000): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const existente = await navigator.serviceWorker.getRegistration();
  if (existente) return existente;
  return Promise.race([
    navigator.serviceWorker.ready.catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}
